import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

export default function RefundsPage() {
  const refundScenarios = [
    {
      title: 'Event Cancelled',
      description: 'If an event is officially cancelled and not rescheduled, you will receive a full refund automatically within 5-7 business days.',
      eligible: true
    },
    {
      title: 'Event Postponed',
      description: 'Your tickets remain valid for the new date. If you cannot attend, contact us within 7 days of the postponement announcement for options.',
      eligible: true
    },
    {
      title: 'Invalid Tickets',
      description: 'If your tickets are rejected at the venue due to being invalid, you will receive a full refund. Contact us immediately with documentation.',
      eligible: true
    },
    {
      title: 'Tickets Not Delivered',
      description: 'If you do not receive your tickets in time for the event, you will receive a full refund or replacement tickets at our discretion.',
      eligible: true
    },
    {
      title: 'Change of Plans',
      description: 'We understand plans change, but all sales are final. Consider reselling your tickets on our platform if you can no longer attend.',
      eligible: false
    },
    {
      title: 'Wrong Tickets Purchased',
      description: 'Please review your order carefully before completing purchase. We cannot refund tickets purchased in error.',
      eligible: false
    }
  ];

  return (
    <>
      <Helmet><title>Refund Policy | BESTIX</title></Helmet>
      <div className="min-h-screen bg-gray-50">
        <div className="bg-navy py-16">
          <div className="container mx-auto px-4 text-center">
            <h1 className="font-playfair text-4xl font-bold !text-white">Refund Policy</h1>
            <p className="mt-4 text-gray-300">Understanding your purchase protection</p>
          </div>
        </div>

        <div className="container mx-auto px-4 py-16">
          <div className="max-w-4xl mx-auto">
            
            {/* Overview */}
            <section className="mb-12">
              <div className="bg-white rounded-xl shadow-md p-8">
                <h2 className="text-2xl font-semibold text-navy mb-4">Our Commitment</h2>
                <p className="text-gray-600 mb-4">
                  At BESTIX, we stand behind every purchase with our comprehensive Buyer Guarantee. While all sales are final in most circumstances, we provide full protection when events don't go as planned.
                </p>
                <p className="text-gray-600">
                  Our goal is simple: you should never pay for an experience you didn't receive.
                </p>
              </div>
            </section>

            {/* Refund Scenarios */}
            <section className="mb-12">
              <h2 className="text-2xl font-semibold text-navy mb-6">Refund Eligibility</h2>
              <div className="space-y-4">
                {refundScenarios.map((scenario, index) => (
                  <div key={index} className={`bg-white rounded-xl shadow-md p-6 border-l-4 ${scenario.eligible ? 'border-green-500' : 'border-gray-300'}`}>
                    <div className="flex items-start gap-4">
                      <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${scenario.eligible ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'}`}>
                        {scenario.eligible ? (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        ) : (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        )}
                      </div>
                      <div>
                        <h3 className="font-semibold text-navy mb-1">{scenario.title}</h3>
                        <p className="text-gray-600 text-sm">{scenario.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Process */}
            <section className="mb-12">
              <h2 className="text-2xl font-semibold text-navy mb-6">Refund Process</h2>
              <div className="bg-white rounded-xl shadow-md p-8">
                <div className="space-y-6">
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-gold rounded-full flex items-center justify-center text-white font-semibold">1</div>
                    <div>
                      <h3 className="font-semibold text-navy mb-1">Contact Us</h3>
                      <p className="text-gray-600 text-sm">Reach out to our support team via email, phone, or live chat with your order details.</p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-gold rounded-full flex items-center justify-center text-white font-semibold">2</div>
                    <div>
                      <h3 className="font-semibold text-navy mb-1">Verification</h3>
                      <p className="text-gray-600 text-sm">We'll verify your claim and may request documentation (e.g., venue rejection notice).</p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-gold rounded-full flex items-center justify-center text-white font-semibold">3</div>
                    <div>
                      <h3 className="font-semibold text-navy mb-1">Resolution</h3>
                      <p className="text-gray-600 text-sm">Approved refunds are processed within 5-7 business days to your original payment method.</p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Timeline */}
            <section className="mb-12">
              <h2 className="text-2xl font-semibold text-navy mb-6">Processing Times</h2>
              <div className="bg-white rounded-xl shadow-md overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-navy">Payment Method</th>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-navy">Processing Time</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-6 py-4 text-gray-600">Credit/Debit Card</td>
                      <td className="px-6 py-4 text-gray-600">5-7 business days</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-gray-600">PayPal</td>
                      <td className="px-6 py-4 text-gray-600">3-5 business days</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-gray-600">Apple Pay / Google Pay</td>
                      <td className="px-6 py-4 text-gray-600">5-7 business days</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            {/* CTA */}
            <section>
              <div className="bg-navy rounded-xl p-8 text-center">
                <h3 className="text-xl font-semibold text-white mb-4">Need to Request a Refund?</h3>
                <p className="text-gray-300 mb-6">Our support team is here to help.</p>
                <Link to="/contact" className="btn btn-primary">
                  Contact Support
                </Link>
              </div>
            </section>

          </div>
        </div>
      </div>
    </>
  );
}
